// src/components/ui/Switch.tsx
'use client';

import React, { forwardRef, InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
}

const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, label, description, size = 'md', disabled, ...props }, ref) => {
    const sizeClasses = {
      sm: { track: 'w-8 h-5', thumb: 'w-3.5 h-3.5', checked: 'peer-checked:translate-x-3' },
      md: { track: 'w-11 h-6', thumb: 'w-5 h-5', checked: 'peer-checked:translate-x-5' },
      lg: { track: 'w-14 h-7', thumb: 'w-6 h-6', checked: 'peer-checked:translate-x-7' },
    } as const;

    const sizes = sizeClasses[size];

    return (
      <label className={cn('inline-flex items-center cursor-pointer', disabled && 'cursor-not-allowed opacity-50', className)}>
        <div className="relative">
          <input type="checkbox" className="sr-only peer" ref={ref} disabled={disabled} {...props} />
          <div
            className={cn(
              sizes.track,
              'bg-muted rounded-full transition-all duration-200',
              'peer-checked:bg-primary',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
              'peer-disabled:cursor-not-allowed peer-disabled:opacity-50'
            )}
          />
          <div
            className={cn(
              sizes.thumb,
              'absolute left-0.5 top-0.5 bg-white rounded-full transition-transform duration-200 shadow-sm',
              sizes.checked
            )}
          />
        </div>
        {(label || description) && (
          <div className="ml-3">
            {label && <span className="text-sm font-medium text-foreground">{label}</span>}
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
        )}
      </label>
    );
  }
);

Switch.displayName = 'Switch';

export { Switch };

